import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { blogArticles } from '../content/blog/articles';
import './ArticleDetailScreen.css';

const ArticleDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const article = blogArticles.find(a => a.id === id);

  if (!article) {
    return (
      <div className="article-detail-screen">
        <div className="container">
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist.</p>
          <Link to="/blog">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Find related articles (same category or tags)
  const relatedArticles = blogArticles
    .filter(a => a.id !== article.id && (
      a.category === article.category || 
      a.tags.some(tag => article.tags.includes(tag))
    ))
    .slice(0, 3);

  return (
    <div className="article-detail-screen" style={{ backgroundColor: theme.colors.background }}>
      <div className="container">
        <button
          onClick={() => navigate('/blog')}
          className="back-button"
          style={{
            color: theme.colors.primary,
            borderColor: theme.colors.primary
          }}
        >
          ← Back to Blog
        </button>

        <article className="article-detail" style={{ backgroundColor: theme.colors.surface }}>
          <header className="article-header">
            {article.featured && (
              <span 
                className="featured-badge"
                style={{ backgroundColor: theme.colors.primary }}
              >
                Featured
              </span>
            )}
            <h1 style={{ color: theme.colors.text }}>{article.title}</h1>
            <p className="article-excerpt-large" style={{ color: theme.colors.textSecondary }}>
              {article.excerpt}
            </p>
            <div className="article-meta-detail">
              <span style={{ color: theme.colors.textSecondary }}>
                By {article.author}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                {new Date(article.date).toLocaleDateString()}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                {article.readTime} min read
              </span>
              <span 
                className="category-badge"
                style={{
                  backgroundColor: theme.colors.primary + '20',
                  color: theme.colors.primary
                }}
              >
                {article.category}
              </span>
            </div>
            <div className="article-tags-detail">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="article-tag"
                  style={{
                    backgroundColor: theme.colors.primary + '20',
                    color: theme.colors.primary
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div 
            className="article-content"
            style={{ color: theme.colors.text }}
          >
            {article.content.split('\n\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index}>
                  {paragraph.split('\n').map((line, lineIndex) => (
                    <React.Fragment key={lineIndex}>
                      {lineIndex > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              )
            ))}
          </div>

          <footer className="article-footer">
            <div className="article-share">
              <h4 style={{ color: theme.colors.text }}>Share this article:</h4>
              <div className="share-buttons">
                <button
                  onClick={() => {
                    navigator.share?.({
                      title: article.title,
                      text: article.excerpt,
                      url: window.location.href
                    }).catch(() => {});
                  }}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: '#fff'
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </footer>
        </article>

        {relatedArticles.length > 0 && (
          <section className="related-articles">
            <h2 style={{ color: theme.colors.text }}>Related Articles</h2>
            <div className="related-articles-grid">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/article/${related.id}`}
                  className="related-article-card"
                  style={{ backgroundColor: theme.colors.surface }}
                >
                  <h3 style={{ color: theme.colors.text }}>{related.title}</h3>
                  <p style={{ color: theme.colors.textSecondary }}>
                    {related.excerpt.substring(0, 100)}...
                  </p>
                  <span style={{ color: theme.colors.primary }}>
                    Read more →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ArticleDetailScreen;
